/**
 * Token Security Utilities
 * SECURITY Option B: Enhanced localStorage with token rotation and monitoring
 */

import { BACKEND_URL } from './apiConfig';

const TOKEN_KEY = 'unified_session_token';
const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000; // Check every 15 minutes
const TOKEN_EXPIRY_KEY = 'token_expiry';

/**
 * Token Manager Class
 * Handles automatic token rotation and security monitoring
 */
class TokenManager {
  constructor() {
    this.refreshInterval = null;
    this.isRefreshing = false;
  }

  /**
   * Start automatic token rotation checks
   */
  startAutoRefresh() {
    if (this.refreshInterval) {
      return; // Already running
    }

    // Check immediately
    this.checkAndRefreshToken();

    // Then check periodically
    this.refreshInterval = setInterval(() => {
      this.checkAndRefreshToken();
    }, TOKEN_REFRESH_INTERVAL);

    console.log('✅ Token auto-refresh started');
  }

  /**
   * Stop automatic token rotation
   */
  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      console.log('Token auto-refresh stopped');
    }
  }

  /**
   * Check if token needs refresh and refresh if needed
   */
  async checkAndRefreshToken() {
    if (this.isRefreshing) {
      return; // Already refreshing
    }

    const token = this.getToken();
    if (!token) {
      return; // No token to refresh
    }

    try {
      this.isRefreshing = true;

      const response = await fetch(`${BACKEND_URL}/api/auth/token/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.rotated && data.new_token) {
          // Token was rotated, update storage
          this.setToken(data.new_token);
          console.log('✅ Token automatically refreshed');
          
          // Dispatch event for components to update
          window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
            detail: { token: data.new_token } 
          }));
        }
      } else if (response.status === 401) {
        // Token invalid, clear and redirect to login
        console.warn('⚠️ Token invalid, logging out');
        this.clearToken();
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Token refresh error:', error);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Validate current token with server
   * Checks for token theft via fingerprint mismatch
   */
  async validateToken() {
    const token = this.getToken();
    if (!token) {
      return { valid: false, reason: 'No token found' };
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/token/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return { valid: true, data };
      } else if (response.status === 401) {
        // Token invalid or fingerprint mismatch
        const error = await response.json().catch(() => ({ detail: 'Token validation failed' }));
        return { valid: false, reason: error.detail };
      }

      return { valid: false, reason: 'Validation failed' };
    } catch (error) {
      console.error('Token validation error:', error);
      return { valid: false, reason: error.message };
    }
  }

  /**
   * Get token from localStorage
   */
  getToken() {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      console.error('Error reading token:', error);
      return null;
    }
  }

  /**
   * Set token in localStorage with security checks
   */
  setToken(token) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      
      // Store expiry timestamp if available (for UI display)
      const expiry = this.extractTokenExpiry(token);
      if (expiry) {
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiry);
      }
    } catch (error) {
      console.error('Error storing token:', error);
    }
  }

  /**
   * Clear token from localStorage
   */
  clearToken() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      this.stopAutoRefresh();
    } catch (error) {
      console.error('Error clearing token:', error);
    }
  }

  /**
   * Extract expiry from JWT token
   */
  extractTokenExpiry(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired() {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) {
      return false; // Can't determine, assume valid
    }

    return Date.now() > parseInt(expiry);
  }

  /**
   * Get time until token expires (in seconds)
   */
  getTimeUntilExpiry() {
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) {
      return null;
    }

    const timeLeft = parseInt(expiry) - Date.now();
    return Math.max(0, Math.floor(timeLeft / 1000));
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();

/**
 * Initialize token security on app load
 * Call this in your App.js or main component
 */
export const initializeTokenSecurity = () => {
  // Start auto-refresh if user is logged in
  if (tokenManager.getToken()) {
    tokenManager.startAutoRefresh();
    
    // Validate token on page load
    tokenManager.validateToken().then(result => {
      if (!result.valid) {
        console.warn('⚠️ Token validation failed:', result.reason);
        if (result.reason && result.reason.includes('theft')) {
          alert('Security alert: Your session may have been compromised. Please log in again.');
        }
        tokenManager.clearToken();
        window.location.href = '/';
      }
    });
  }

  // Listen for storage events (multi-tab token sync)
  window.addEventListener('storage', (e) => {
    if (e.key === TOKEN_KEY) {
      if (e.newValue) {
        // Token updated in another tab, restart auto-refresh
        tokenManager.stopAutoRefresh();
        tokenManager.startAutoRefresh();
      } else {
        // Token cleared in another tab
        tokenManager.stopAutoRefresh();
        window.location.href = '/';
      }
    }
  });

  console.log('✅ Token security initialized');
};

/**
 * Hook for React components to use token manager
 * Usage: const token = useToken();
 */
export const useToken = () => {
  return {
    getToken: () => tokenManager.getToken(),
    setToken: (token) => tokenManager.setToken(token),
    clearToken: () => tokenManager.clearToken(),
    validateToken: () => tokenManager.validateToken(),
    isExpired: () => tokenManager.isTokenExpired(),
    timeUntilExpiry: () => tokenManager.getTimeUntilExpiry()
  };
};

// Default export
export default tokenManager;
